import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import BottomNav from "../components/BottomNav";
import { ArrowLeft, Trash2, ShieldOff, FileText, Lock, Bell, LogOut, AlertTriangle, ChevronRight, X } from "lucide-react";

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [blocked, setBlocked] = useState([]);
  const [showDelete, setShowDelete] = useState(false);
  const [marketingOpt, setMarketingOpt] = useState(!!user?.marketing_opt_in);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blocks");
        setBlocked(data);
      } catch (err) { console.error("Failed to load blocked users", err); }
    })();
  }, []);

  const unblock = async (id) => {
    try {
      await api.delete(`/block/${id}`);
      setBlocked((b) => b.filter((u) => u.id !== id));
      await refresh();
    } catch (err) { console.error("Failed to unblock user", err); }
  };

  const toggleMarketing = async (val) => {
    setMarketingOpt(val);
    try {
      await api.patch("/profile", { marketing_opt_in: val });
    } catch (err) {
      console.error("Failed to update marketing preference", err);
      setMarketingOpt(!val);  // revert on failure
    }
  };

  const out = async () => { await logout(); nav("/"); };

  if (!user) return null;

  return (
    <div className="app-shell bg-aurora min-h-screen pb-nav" data-testid="settings-page">
      <div className="px-6 pt-10 pb-2 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow-sm" data-testid="settings-back">
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-display text-3xl text-[#332D38]">Nastavení</h1>
      </div>

      <div className="px-6 pt-4 space-y-3">
        {/* Account */}
        <div className="glass-card p-4">
          <p className="label-soft mb-2">Účet</p>
          <div className="text-sm text-[#54635F]">
            <div className="flex justify-between py-1.5"><span>Jméno</span><span className="text-[#332D38]">{user.name}</span></div>
            <div className="flex justify-between py-1.5"><span>E-mail</span><span className="text-[#332D38]">{user.email}</span></div>
            <div className="flex justify-between py-1.5"><span>Věk</span><span className="text-[#332D38]">{user.age}</span></div>
            <div className="flex justify-between py-1.5"><span>Verze</span><span className="text-[#332D38]">Sentra ✦ Plná verze</span></div>
          </div>
        </div>

        {/* Notifications */}
        <div className="glass-card p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#FFE4CC] flex items-center justify-center shrink-0"><Bell size={16} className="text-[#E85D04]" /></div>
          <div className="flex-1">
            <p className="font-medium text-[#332D38]">Marketingové novinky</p>
            <p className="text-xs text-[#8A7E91] mb-2">Tipy pro vztahy, novinky Sentra (nepovinné)</p>
            <button onClick={() => toggleMarketing(!marketingOpt)} data-testid="toggle-marketing"
              className={`relative w-12 h-7 rounded-full transition ${marketingOpt ? "bg-[#0F3D34]" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${marketingOpt ? "left-5" : "left-0.5"}`} />
            </button>
          </div>
        </div>

        {/* Blocked users */}
        <div className="glass-card p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#FFE4CC] flex items-center justify-center"><ShieldOff size={16} className="text-[#E85D04]" /></div>
            <div className="flex-1">
              <p className="font-medium text-[#332D38]">Zablokovaní uživatelé</p>
              <p className="text-xs text-[#8A7E91]">{blocked.length === 0 ? "Nikdo zablokován" : `${blocked.length} osob`}</p>
            </div>
          </div>
          {blocked.length > 0 && (
            <div className="space-y-2 mt-2" data-testid="blocked-list">
              {blocked.map((u) => (
                <div key={u.id} className="flex items-center gap-3 p-2 rounded-2xl bg-white/60">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-[#FFE4CC]">
                    {u.photo_url ? <img src={u.photo_url} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <span className="flex-1 text-sm text-[#332D38]">{u.name}</span>
                  <button onClick={() => unblock(u.id)} className="text-xs px-3 py-1.5 rounded-full bg-[#0F3D34] text-white" data-testid={`unblock-${u.id}`}>Odblokovat</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Legal */}
        <Link to="/terms" className="glass-card p-4 flex items-center gap-3" data-testid="link-terms">
          <div className="w-9 h-9 rounded-full bg-[#FFE4CC] flex items-center justify-center"><FileText size={16} className="text-[#E85D04]" /></div>
          <div className="flex-1"><p className="font-medium text-[#332D38]">Podmínky použití</p></div>
          <ChevronRight size={18} className="text-[#8A7E91]" />
        </Link>
        <Link to="/privacy" className="glass-card p-4 flex items-center gap-3" data-testid="link-privacy">
          <div className="w-9 h-9 rounded-full bg-[#FFE4CC] flex items-center justify-center"><Lock size={16} className="text-[#E85D04]" /></div>
          <div className="flex-1"><p className="font-medium text-[#332D38]">Ochrana osobních údajů</p></div>
          <ChevronRight size={18} className="text-[#8A7E91]" />
        </Link>

        {/* Logout */}
        <button onClick={out} className="w-full py-4 rounded-3xl text-[#54635F] border border-slate-200 bg-white flex items-center justify-center gap-2 mt-2" data-testid="settings-logout">
          <LogOut size={16} /> Odhlásit se
        </button>

        {/* Danger zone */}
        <div className="rounded-3xl p-4 border border-rose-200 bg-rose-50/60 mt-4">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle size={18} className="text-rose-500" />
            <p className="font-medium text-rose-700">Nebezpečná zóna</p>
          </div>
          <p className="text-xs text-rose-800/80 mb-3">Smazání účtu je trvalé. Všechny tvé zprávy, matche, dotazník a analýzy budou nenávratně smazány.</p>
          <button onClick={() => setShowDelete(true)} className="w-full py-3 rounded-full bg-rose-600 text-white text-sm font-medium flex items-center justify-center gap-2" data-testid="delete-account-btn">
            <Trash2 size={16} /> Smazat účet trvale
          </button>
        </div>
      </div>

      {showDelete && <DeleteAccountModal onClose={() => setShowDelete(false)} />}

      <BottomNav />
    </div>
  );
}

function DeleteAccountModal({ onClose }) {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError("");
    if (confirm !== "SMAZAT") { setError("Napiš přesně 'SMAZAT' pro potvrzení."); return; }
    if (!password) { setError("Zadej své heslo."); return; }
    setLoading(true);
    try {
      await api.delete("/account", { data: { password, reason } });
      try { localStorage.removeItem("sentra_token"); } catch (e) { /* legacy cleanup */ }
      await logout();
      nav("/");
    } catch (e) {
      setError(e.response?.data?.detail || "Nepodařilo se smazat účet.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="delete-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-rose-700">Smazat účet</h3>
          <button onClick={onClose} className="text-[#8A7E91]" data-testid="delete-close"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#54635F] mb-4">Tato akce je <strong>nevratná</strong>. Pro potvrzení napiš „SMAZAT“ a zadej své heslo.</p>
        <div className="space-y-3">
          <input className="input-field" placeholder="Napiš SMAZAT" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="delete-confirm" />
          <input type="password" className="input-field" placeholder="Tvé heslo" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="delete-password" />
          <textarea rows={2} className="input-field" placeholder="Proč odcházíš? (nepovinné)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="delete-reason" />
        </div>
        {error && <div className="text-sm text-rose-600 mt-3">{error}</div>}
        <div className="flex gap-3 mt-4">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-slate-200 text-[#54635F]" data-testid="delete-cancel">Zrušit</button>
          <button onClick={submit} disabled={loading} className="flex-1 py-3 rounded-full bg-rose-600 text-white font-medium" data-testid="delete-submit">
            {loading ? "Mažu..." : "Smazat trvale"}
          </button>
        </div>
      </div>
    </div>
  );
}
